import {
  PVP_ACCEPT_ROUTE,
  PVP_ACTIVE_ROUTE,
  PVP_CHALLENGE_ROUTE,
  PVP_LEAVE_ROUTE,
  PVP_SOCKET_ROUTE,
  PVP_TICKET_ROUTE,
  type PvpAcceptResponse,
  type PvpActiveResponse,
  type PvpChallengeResponse,
  type PvpLeaveResponse,
  type PvpTicketResponse,
  pvpAcceptResponseSchema,
  pvpActiveResponseSchema,
  pvpChallengeResponseSchema,
  pvpLeaveResponseSchema,
  pvpTicketResponseSchema,
} from '@kenocyber/shared';
import { API_BASE_URL } from '../auth/client';

/**
 * The duel's three authenticated POSTs, and the URL of its socket.
 *
 * ## Why the socket ticket is fetched over HTTP at all
 *
 * A WebSocket handshake is not covered by CORS and carries cookies by
 * `SameSite`, and this client and this API are on different sites in every
 * environment that exists. So the browser proves who it is the ordinary way -
 * a cookie on a cross-origin POST - and receives a short-lived capability to
 * put in the socket's query string. The server frozen at `d9cf24a` will not
 * open a socket any other way.
 *
 * The ticket is **single use and lives thirty seconds**. Every connection and
 * every reconnection mints a fresh one; a client that kept one and presented it
 * twice would be refused by the room, which is exactly the point of it.
 *
 * ## What this module deliberately does not do
 *
 * It does not retry, does not queue, and does not decide what a failure means.
 * A duel is a thing a person chose to start; a client that silently re-attempts
 * to seat somebody who has walked away is a client that plays for them.
 */

/** A refusal from a duel route. A stable machine reason, never a sentence. */
export class PvpApiError extends Error {
  readonly status: number;
  /** The API's machine reason, when it sent one. */
  readonly reason: string | null;

  constructor(status: number, reason: string | null) {
    super(`pvp api error ${status}`);
    this.name = 'PvpApiError';
    this.status = status;
    this.reason = reason;
  }

  /** The feature is off, or its runtime is absent. Stop; do not retry. */
  get isDisabled(): boolean {
    return this.reason === 'pvp_disabled' || this.reason === 'pvp_gold_pot_unavailable';
  }

  /**
   * Unknown, ended, or none of the caller's business.
   *
   * The server answers all three the same way on purpose, so a code cannot be
   * used to enumerate live rooms. The client must not try to tell them apart
   * either - it says the challenge is not available and stops.
   */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Already in a live duel, or the room filled up first. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/**
 * One authenticated POST, and one way a refusal becomes a {@link PvpApiError}.
 *
 * Exported because the TEAM lobby's routes are the same kind of request against
 * the same API with the same cookie and the same failure mapping - and a second
 * copy of this function would be a second opinion about what a 404 means. What
 * is NOT shared is the routes and the shapes: those live beside the mode that
 * owns them, in `pvp.ts` and `pvp-team.ts` respectively.
 */
export async function pvpPost<T>(
  path: string,
  body: unknown,
  parse: (value: unknown) => T,
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    // The httpOnly session cookie is the only credential. Nothing token-like is
    // stored anywhere JavaScript can read it.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const failure = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new PvpApiError(response.status, failure?.error?.message ?? null);
  }
  return parse(await response.json());
}

/** The same, for a read. Exported for the same reason as {@link pvpPost}. */
export async function pvpGet<T>(path: string, parse: (value: unknown) => T): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'GET',
    // The httpOnly session cookie is the only credential, exactly as `post`.
    credentials: 'include',
  });
  if (!response.ok) {
    const failure = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new PvpApiError(response.status, failure?.error?.message ?? null);
  }
  return parse(await response.json());
}

/**
 * Whether the signed-in account is already in a duel.
 *
 * Asked once when the app mounts, and the only way a reloaded tab can find
 * out which match it was in. Nothing about the answer is trusted as an
 * authorisation: the ticket route re-checks the seat against the database,
 * and the room re-checks the ticket.
 */
export async function findActivePvpMatch(): Promise<PvpActiveResponse['match']> {
  const answer = await pvpGet(PVP_ACTIVE_ROUTE, (value) => pvpActiveResponseSchema.parse(value));
  return answer.match;
}

/** Opens a private challenge and mints its code. Seat `a`, always. */
export async function createPvpChallenge(): Promise<PvpChallengeResponse> {
  /*
   * The mode is sent explicitly and is always `no_reward`.
   *
   * The request schema has no default, deliberately: a request that forgot to
   * say what it wanted would otherwise get a mode chosen for it, and one of the
   * two modes moves Gold. There is no UI that can produce any other value.
   */
  return pvpPost(PVP_CHALLENGE_ROUTE, { mode: 'no_reward' }, (value) =>
    pvpChallengeResponseSchema.parse(value),
  );
}

/**
 * Gives up a duel that has not started, from outside the room (gate V27A).
 *
 * The panel's way out of `already_in_match`. Leaving a duel is otherwise an
 * intent sent over the socket, from inside the room - which is unreachable
 * exactly when a player most needs out, because the room is what died.
 *
 * It cannot end a fight: the server refuses a `running` match, and that
 * refusal is the point rather than an inconvenience.
 */
export async function leavePvpDuel(): Promise<PvpLeaveResponse> {
  return pvpPost(PVP_LEAVE_ROUTE, {}, (value) => pvpLeaveResponseSchema.parse(value));
}
/** Takes the second seat with a code somebody handed out. */
export async function acceptPvpChallenge(code: string): Promise<PvpAcceptResponse> {
  return pvpPost(PVP_ACCEPT_ROUTE, { code }, (value) => pvpAcceptResponseSchema.parse(value));
}

/**
 * Mints one capability to open one socket.
 *
 * The seat comes back from the DATABASE - the caller says which match and the
 * server says which seat, or refuses. There is no request field a client could
 * use to ask for the other one.
 */
export async function mintPvpSocketTicket(matchId: string): Promise<PvpTicketResponse> {
  return pvpPost(PVP_TICKET_ROUTE, { matchId }, (value) => pvpTicketResponseSchema.parse(value));
}

/**
 * Where the duel socket lives, with the capability in the query string.
 *
 * `http` becomes `ws` and `https` becomes `wss` off the SAME base the rest of
 * the API uses, so there is one place a deployment is configured rather than
 * two that can disagree. The ticket is encoded because it is a signed payload
 * with `+`, `/` and `=` already replaced - but a URL builder must not depend on
 * that having been done correctly somewhere else.
 */
export function pvpSocketUrl(ticket: string): string {
  const base = new URL(API_BASE_URL);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = PVP_SOCKET_ROUTE;
  base.search = `?ticket=${encodeURIComponent(ticket)}`;
  return base.toString();
}
